import * as Sentry from '@sentry/angular';
import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '@core/loading.service';
import { Subscription, TimeoutError } from 'rxjs';
import { filter, map, take, timeout } from 'rxjs/operators';
import { SliderManagerService } from '../../services/slider-manager.service';

@Component({
  selector: 'app-slider-manager',
  template: '',
  styleUrls: ['./slider-manager.component.css'],
  standalone: true,
  imports: [],
})
export class SliderManagerComponent implements OnDestroy {

  private sliderSubscription?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sliderManagerService: SliderManagerService,
    private loadingService: LoadingService
  ) {
    this.loadingService.present();
    this.sliderManagerService.trackLastRoute();
  }

  ionViewWillEnter() {
    this.sliderSubscription?.unsubscribe();

    // Use the route snapshot to read the fragment synchronously. By the time
    // ionViewWillEnter fires, Angular has already committed the navigation and
    // the snapshot reflects the final, settled URL — so there is no risk of
    // capturing a transient null before the real fragment is available.
    const fragment = this.route.snapshot.fragment;

    if (!fragment) {
      // No hash fragment in the URL (e.g. a push-notification link that only
      // carries query params). Redirect home immediately rather than waiting
      // for the 8-second timeout to fire.
      this.loadingService.dismiss();
      this.router.navigateByUrl("/mainTabs/tabs/home");
      return;
    }

    // Fragment is confirmed. Watch only sliderManagerSlidesData and carry the
    // captured fragment through via map — avoids any re-read of route.fragment
    // and keeps the chain stable even if the URL were to change mid-flight.
    this.sliderSubscription = this.sliderManagerService.sliderManagerSlidesData.pipe(
      filter((d) =>
        !!d &&
        Object.keys(d).length > 0 &&
        Object.prototype.hasOwnProperty.call(d, fragment)
      ),
      take(1),
      // Safety net: redirect home if valid state never arrives (slow network,
      // Firestore permission error, key missing from document).
      timeout(8000),
      map((d) => [d, fragment] as const),
    ).subscribe({
      next: ([d, frag]) => {
        const alias = frag.split("-")[2] || "";
        this.sliderManagerService.alias$.next(alias);
        const slideUrl = (d as Record<string, unknown>)[frag];
        if (typeof slideUrl !== "string" || !slideUrl.trim()) {
          this.loadingService.dismiss();
          this.router.navigateByUrl("/mainTabs/tabs/home");
          return;
        }

        this.loadingService.dismiss();
        this.sliderManagerService?.currentSlideData?.next(frag);
        const urlParts = slideUrl.split("/").filter(Boolean);
        const nextPageUrl = urlParts.length ? "/" + urlParts[urlParts.length - 1] : "/mainTabs/tabs/home";

        this.router.navigateByUrl(nextPageUrl).catch(() => {
          this.loadingService.dismiss();
          this.router.navigateByUrl("/mainTabs/tabs/home");
        });
      },
      error: (err) => {
        if (err instanceof TimeoutError) {
          // Timeout here means the fragment key was present but Firestore
          // never returned data containing it — a genuine network/data issue.
          Sentry.captureMessage("SliderManager: timed out waiting for slide data", {
            level: "warning",
            tags: { component: "slider-manager" },
          });
        } else {
          Sentry.captureException(err, { tags: { component: "slider-manager" } });
        }
        this.loadingService.dismiss();
        this.router.navigateByUrl("/mainTabs/tabs/home");
      },
    });
  }

  ionViewWillLeave() {
    this.sliderSubscription?.unsubscribe();
    this.sliderManagerService.clearLastRouteSubscription();
  }

  ngOnDestroy() {
    this.sliderSubscription?.unsubscribe();
  }
}